import React, { createContext, useContext, useEffect, useState } from 'react';
import { Brand } from '../types/brand';
import { useGetBrands } from '../api/brands';

interface BrandContextType {
  brands: Brand[];
  brandsLoading: boolean;
  getBrandName: (brandId: number) => string | undefined;
  getBrand: (brandId: number) => Brand | undefined;
}

const BrandContext = createContext<BrandContextType | undefined>(undefined);

export const useBrands = () => {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrands must be used within a BrandProvider');
  }
  return context;
};

interface BrandProviderProps {
  children: React.ReactNode;
}

export const BrandProvider: React.FC<BrandProviderProps> = ({ children }) => {
  const { brandsData, brandsIsLoading } = useGetBrands();
  const [brands, setBrands] = useState<Brand[]>([]);

  useEffect(() => {
    if (brandsData) {
      setBrands(brandsData);
    }
  }, [brandsData]);

  const getBrandName = (brandId: number): string | undefined => {
    const brand = brands.find(b => b.id === brandId);
    return brand?.name;
  };

  const getBrand = (brandId: number): Brand | undefined => {
    return brands.find(b => b.id === brandId);
  };

  const value: BrandContextType = {
    brands,
    brandsLoading: brandsIsLoading,
    getBrandName,
    getBrand,
  };

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
};
